import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	computedFrom,
	customElement,
	observable,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { bindable } from 'aurelia-templating';

import 'bootstrap';
import 'bootstrap-select';

import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import { PresentationType } from '~/converters/presentation-type';
import { CodeAndDescription } from '~/models/code-and-description';

const styles = {
	dropdown: 'a-multiselect__dropdown',
	button: 'a-multiselect__button',
	buttonFilterOption: 'a-multiselect__button__filter-option',
	buttonFilterOptionInner: 'a-multiselect__button__filter-option-inner',
	buttonFilterOptionInnerInner:
		'a-multiselect__button__filter-option-inner-inner',
	dropdownMenuList: 'a-multiselect__dropdown-menu-list',
	dropdownItem: 'a-multiselect__dropdown-item',
	dropdownItemIcon: 'a-multiselect__icon',
	dropdownItemIconSelected: 'a-multiselect__icon--selected',
	dropdownItemText: 'a-multiselect__text',
	isOpenedInFilter: 'a-multiselect--is-opened-in-filter',
};

@autoinject
@customElement('a-multiselect')
@useView(PLATFORM.moduleName('./multiselect.html'))
export class Multiselect extends Field {
	/**
	 * If "fieldModel" is not set, this property can be used to configure the available options a user
	 * can select from.
	 */
	@bindable private options?: CodeAndDescription[];

	@bindable protected isLabelLeft: boolean = false;

	@bindable public presentationType?: PresentationType;

	private isInitialized: boolean = false;

	constructor(
		public element: Element,
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
	) {
		super(Field.TYPE.MULTISELECT, element, parentForm, eventAggregator);
		// NOTE: Jake: 2019-01-17 - WEBUI-1239
		// This was formerly known as "isFilterField" but was only used by <a-select> when
		// it was a multiselect.
		//
		// This logic hides the label and ensures the "fieldId" is calculated without
		// prepending the parent form name in field.ts.
		// At some point in the future I want to remove this property completely.
		this.isMultiselectField = true;
		this.isDisableInputMasks = true;
	}

	public attached() {
		super.attached();
		if (this.refInputElem && this.value) {
			// WEBUI-2382: If multiselect has initial selected options, can't wait for
			// `changed.bs.select` event is triggered because this event listener is added
			// after attached in `selectElChanged`
			this.tryInitSelectpicker();
		}
	}

	@computedFrom('options', 'fieldModel', 'fieldModel.codeList')
	private get selectOptions(): CodeAndDescription[] {
		if (this.fieldModel) {
			throw new Error(
				'Using a fieldModel with an <a-multiselect> is undefined behaviour.',
			);
		} else {
			if (this.isInitialized && this.refInputElem) {
				// This method is called when rendering the selected options from the template.
				// We are using bootstrap select and it needs to be refreshed after aurelia has
				// finished adding the options to the page.
				window.requestAnimationFrame(() =>
					$(this.refInputElem).selectpicker('refresh'),
				);
			}
			return this.options;
		}
	}

	protected valueChanged(value, oldValue) {
		super.valueChanged(value, oldValue);
		if (this.isInitialized && this.refInputElem) {
			$(this.refInputElem).selectpicker('val', this.value);
			// NOTE: Jake: 2019-01-25 - WEBUI-1359
			// Update classes so the icons are checked
			this.updateSelectedItemClasses();
		}
	}

	/**
	 * @override
	 */
	protected inputElemChanged(
		selectEl?: HTMLSelectElement,
		oldSelectEl?: HTMLSelectElement,
	) {
		if (selectEl) {
			// NOTE: Jake: 2019-01-25 - WEBUI-1359
			// No <option> items rendered without wrapping in this taskQueue or $(document).ready()
			// This "selectElChanged" event also fires after "attached()", so it's not a timing issue related
			// to that either.
			this.tryInitSelectpicker();
		}
		super.inputElemChanged(selectEl, oldSelectEl);
	}

	private tryInitSelectpicker(): void {
		if (this.isInitialized) {
			return;
		}
		const selectEl = this.refInputElem;
		if (!selectEl) {
			return;
		}
		$(selectEl).on('show.bs.select', this.onBootstrapSelectShow);
		$(selectEl).on('changed.bs.select', () =>
			this.updateSelectedItemClasses(),
		);
		$(selectEl).on('shown.bs.select', this.onBootstrapDropdownOpened);
		$(selectEl).on('hidden.bs.select', this.onBootstrapDropdownClosed);
		$(selectEl).selectpicker({
			// NOTE: Jake: 2019-01-25 - WEBUI-1359
			// Refer to docs: https://developer.snapappointments.com/bootstrap-select/options/
			dropdownAlignRight: true,
			dropupAuto: false, // force the dropdown to appear underneath
			noneSelectedText: '',
			selectOnTab: true,
			styleBase: styles.button,
			size: 10,
		});

		// updateDropdownAndButtonClasses
		{
			// Setup dropdown-menu
			const containerEl = this.refInputElem.parentElement;
			containerEl.classList.add(styles.dropdown);

			// Update button
			const buttonTextEl = containerEl.querySelector(
				'.filter-option-inner',
			);
			if (buttonTextEl) {
				buttonTextEl.classList.add(styles.buttonFilterOptionInner);
			}
		}

		this.updateSelectedItemClasses();

		this.isInitialized = true;
	}

	/**
	 * Iterate over each item in the bootstrap select and update the icon element
	 * to have a selected class (remove that class from all other icons)
	 */
	private updateSelectedItemClasses = (): void => {
		const containerEl = this.refInputElem.parentElement;

		// Reset icon selected status on each option
		const iconElList = containerEl.querySelectorAll(
			`.${styles.dropdownItemIconSelected}`,
		);
		iconElList.forEach((iconEl) => {
			iconEl.classList.remove(styles.dropdownItemIconSelected);
		});

		// Setup button styling
		{
			const filterOptionEl = containerEl.querySelector('.filter-option');
			if (!filterOptionEl) {
				throw new Error('Unable to find .filter-option');
			}
			filterOptionEl.classList.add(styles.buttonFilterOption);

			const filterOptionInnerEl = containerEl.querySelector(
				'.filter-option-inner',
			);
			if (!filterOptionInnerEl) {
				throw new Error('Unable to find .filter-option-inner');
			}
			filterOptionInnerEl.classList.add(styles.buttonFilterOptionInner);

			const filterOptionInnerInnerEl = containerEl.querySelector(
				'.filter-option-inner-inner',
			);
			if (!filterOptionInnerInnerEl) {
				throw new Error('Unable to find .filter-option-inner-inner');
			}
			filterOptionInnerInnerEl.classList.add(
				styles.buttonFilterOptionInnerInner,
			);
		}

		const dropdownMenuEl = containerEl.querySelector('ul.dropdown-menu');
		if (!dropdownMenuEl) {
			throw new Error('Unable to find ul.dropdown-menu');
		}
		dropdownMenuEl.classList.add(styles.dropdownMenuList);

		// Update all icons to have selected class
		const dropdownMenuItems = containerEl.querySelectorAll(
			`.${styles.dropdownItem}.selected`,
		);
		dropdownMenuItems.forEach((dropdownMenuItem) => {
			const iconEl = dropdownMenuItem.querySelector(
				`.${styles.dropdownItemIcon}`,
			);
			if (iconEl) {
				iconEl.classList.add(styles.dropdownItemIconSelected);
			}
		});
	};

	private onBootstrapSelectShow = (): void => {
		const containerEl = this.refInputElem.parentElement;
		const dropdownMenuItems =
			containerEl.querySelectorAll('.dropdown-item');
		dropdownMenuItems.forEach((dropdownMenuItem) => {
			dropdownMenuItem.classList.add(styles.dropdownItem);

			const iconEl = dropdownMenuItem.querySelector('.check-mark');
			if (iconEl) {
				iconEl.classList.add(styles.dropdownItemIcon);
			}

			const textEl = dropdownMenuItem.querySelector('.text');
			if (textEl) {
				textEl.classList.add(styles.dropdownItemText);
			}
		});

		// NOTE: Jake: 2019-01-25 - WEBUI-1359
		// Update classes so the icons are checked (ie. if value is prepopulated)
		this.updateSelectedItemClasses();
	};

	private onBootstrapDropdownOpened = (): void => {
		// NOTE: Jake: 2019-01-25 - WEBUI-1359
		// Bit of a hack to make sure the z-index of the opened bootstrap-select item
		// is rendered over other filter items.
		document
			.querySelectorAll('.a-filter__options__option')
			.forEach((el) => {
				if (el.contains(this.element)) {
					el.classList.add(styles.isOpenedInFilter);
				}
			});
	};

	private onBootstrapDropdownClosed = (): void => {
		document
			.querySelectorAll('.a-filter__options__option')
			.forEach((el) => {
				if (el.contains(this.element)) {
					el.classList.remove(styles.isOpenedInFilter);
				}
			});
	};
}
